/**
 * Real-time Notifications Hook
 * React hook for handling real-time notifications and WebSocket connections
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import notificationClient from '../utils/notificationClient';

console.log('[DEBUG] useRealTimeNotifications module loaded');

export const useRealTimeNotifications = (token) => {
    const [isConnected, setIsConnected] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [connectionError, setConnectionError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastNotification, setLastNotification] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');

    const notificationCallbackRef = useRef(null);
    const isInitializedRef = useRef(false);

    // Initialize connection
    useEffect(() => {
        if (token) {
            console.log('[useRealTimeNotifications] Initializing connection with token...');
            isInitializedRef.current = true;

            // Connect to WebSocket server
            notificationClient.connect(token);

            // Request initial notifications
            setTimeout(() => {
                notificationClient.requestNotifications();
            }, 1000);
        } else {
            console.log('[useRealTimeNotifications] No token available, skipping connection...');
        }

        return () => {
            if (isInitializedRef.current) {
                console.log('[useRealTimeNotifications] Cleaning up connection...');
                notificationClient.disconnect();
                isInitializedRef.current = false;
            }
        };
    }, [token]);

    // Handle new notifications
    const handleNewNotification = useCallback((notification) => {
        console.log('[useRealTimeNotifications] New notification received:', notification);

        setNotifications(prev => [notification, ...prev]);
        setLastNotification(notification);

        if (!notification.read) {
            setUnreadCount(prev => prev + 1);
        }
    }, []);

    // Handle notifications update
    const handleNotificationsUpdate = useCallback((notificationsList) => {
        console.log('[useRealTimeNotifications] Notifications update:', notificationsList.length);

        setNotifications(notificationsList);

        const unread = notificationsList.filter(n => !n.read).length;
        setUnreadCount(unread);
    }, []);

    // Handle connection events
    useEffect(() => {
        // Connection established
        const handleConnectionEstablished = (data) => {
            console.log('[useRealTimeNotifications] Connection established:', data);
            setIsConnected(true);
            setConnectionError(null);
            setConnectionStatus('connected');
        };

        // Connection error
        const handleConnectionError = (error) => {
            console.error('[useRealTimeNotifications] Connection error:', error);
            setIsConnected(false);
            setConnectionError(error.error);
            setConnectionStatus('error');
        };

        // Disconnected
        const handleDisconnected = (data) => {
            console.log('[useRealTimeNotifications] Disconnected:', data);
            setIsConnected(false);
            setConnectionStatus('disconnected');
        };

        // Reconnected
        const handleReconnected = (data) => {
            console.log('[useRealTimeNotifications] Reconnected:', data);
            setIsConnected(true);
            setConnectionError(null);
            setConnectionStatus('connected');

            // Request notifications after reconnection
            notificationClient.requestNotifications();
        };

        // Reconnect failed
        const handleReconnectFailed = () => {
            console.error('[useRealTimeNotifications] Reconnect failed');
            setIsConnected(false);
            setConnectionError('Failed to reconnect to server');
            setConnectionStatus('failed');
        };

        // Set up event listeners
        notificationClient.on('connection-established', handleConnectionEstablished);
        notificationClient.on('connection-error', handleConnectionError);
        notificationClient.on('disconnected', handleDisconnected);
        notificationClient.on('reconnected', handleReconnected);
        notificationClient.on('reconnect-failed', handleReconnectFailed);
        notificationClient.on('new-notification', handleNewNotification);
        notificationClient.on('notifications-update', handleNotificationsUpdate);

        // Add notification callback
        notificationCallbackRef.current = handleNewNotification;
        notificationClient.onNotification(notificationCallbackRef.current);

        return () => {
            // Clean up event listeners
            notificationClient.off('connection-established', handleConnectionEstablished);
            notificationClient.off('connection-error', handleConnectionError);
            notificationClient.off('disconnected', handleDisconnected);
            notificationClient.off('reconnected', handleReconnected);
            notificationClient.off('reconnect-failed', handleReconnectFailed);
            notificationClient.off('new-notification', handleNewNotification);
            notificationClient.off('notifications-update', handleNotificationsUpdate);

            // Remove notification callback
            if (notificationCallbackRef.current) {
                notificationClient.offNotification(notificationCallbackRef.current);
            }
        };
    }, [handleNewNotification, handleNotificationsUpdate]);

    // Mark notification as read
    const markAsRead = useCallback((notificationId) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif.id === notificationId
                    ? { ...notif, read: true }
                    : notif
            )
        );

        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(() => {
        setNotifications(prev =>
            prev.map(notif => ({ ...notif, read: true }))
        );
        setUnreadCount(0);
    }, []);

    // Clear notifications
    const clearNotifications = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
        setLastNotification(null);
    }, []);

    // Request latest notifications
    const refreshNotifications = useCallback(() => {
        if (isConnected) {
            notificationClient.requestNotifications();
        }
    }, [isConnected]);

    // Send typing indicator
    const startTyping = useCallback(() => {
        notificationClient.startTyping();
    }, []);

    const stopTyping = useCallback(() => {
        notificationClient.stopTyping();
    }, []);

    // Update user status
    const updateStatus = useCallback((status) => {
        notificationClient.updateStatus(status);
    }, []);

    // Ping server
    const ping = useCallback(() => {
        notificationClient.ping();
    }, []);

    // Subscribe to specific events
    const subscribe = useCallback((event, callback) => {
        notificationClient.on(event, callback);

        return () => {
            notificationClient.off(event, callback);
        };
    }, []);

    return {
        // Connection state
        isConnected,
        connectionError,
        connectionStatus,

        // Notifications
        notifications,
        unreadCount,
        lastNotification,

        // Actions
        markAsRead,
        markAllAsRead,
        clearNotifications,
        refreshNotifications,

        // Communication
        startTyping,
        stopTyping,
        updateStatus,
        ping,

        // Events
        subscribe
    };
};

// Hook for specific notification types
export const useTaskNotifications = (token) => {
    const [taskAssignments, setTaskAssignments] = useState([]);
    const [statusUpdates, setStatusUpdates] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);

    const { subscribe } = useRealTimeNotifications(token);

    useEffect(() => {
        const unsubscribeAssignment = subscribe('task-assigned', (data) => {
            setTaskAssignments(prev => [data, ...prev.slice(0, 9)]); // Keep last 10
        });

        const unsubscribeStatus = subscribe('status-update', (data) => {
            setStatusUpdates(prev => [data, ...prev.slice(0, 9)]);
        });

        const unsubscribeCompleted = subscribe('request-completed', (data) => {
            setCompletedTasks(prev => [data, ...prev.slice(0, 9)]);
        });

        return () => {
            unsubscribeAssignment();
            unsubscribeStatus();
            unsubscribeCompleted();
        };
    }, [subscribe]);

    return {
        taskAssignments,
        statusUpdates,
        completedTasks
    };
};

// Hook for inventory notifications
export const useInventoryNotifications = (token) => {
    const [inventoryAlerts, setInventoryAlerts] = useState([]);
    const [lowStockCount, setLowStockCount] = useState(0);

    const { subscribe } = useRealTimeNotifications(token);

    useEffect(() => {
        const unsubscribe = subscribe('inventory-alert', (data) => {
            setInventoryAlerts(prev => {
                const newAlerts = [data, ...prev.slice(0, 19)]; // Keep last 20
                setLowStockCount(newAlerts.length);
                return newAlerts;
            });
        });

        return unsubscribe;
    }, [subscribe]);

    return {
        inventoryAlerts,
        lowStockCount
    };
};

export default useRealTimeNotifications;
